import React from 'react';

export const UIDemo = ({ colors }) => {
    const getColor = (index) => colors[index]?.value || colors[0]?.value || '#3b82f6';

    // Convert hex to RGB for transparency effects
    const hexToRgba = (hex, alpha = 1) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return (
        <div className="space-y-8">
            <div className="text-center">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">UI Design System</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Complete component library with consistent styling, states, and interactions using your color palette.
                </p>
            </div>

            {/* Button System */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Button System</h5>

                <div className="space-y-8">
                    {/* Button Variants */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">BUTTON VARIANTS</span>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                {/* Primary Buttons */}
                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Primary Actions</h6>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90 focus:ring-4 focus:outline-none"
                                                style={{
                                                    backgroundColor: getColor(0),
                                                    boxShadow: `0 4px 14px 0 ${hexToRgba(getColor(0), 0.25)}`
                                                }}
                                            >
                                                Primary Large
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Default state</span>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-5 py-2.5 rounded-md font-medium text-white transition-all hover:opacity-90"
                                                style={{ backgroundColor: getColor(0) }}
                                            >
                                                Primary Medium
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Medium size</span>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-4 py-2 rounded-md font-medium text-white transition-all hover:opacity-90"
                                                style={{ backgroundColor: getColor(0) }}
                                            >
                                                Primary Small
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Compact</span>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-6 py-3 rounded-lg font-semibold text-white opacity-50 cursor-not-allowed"
                                                style={{ backgroundColor: getColor(0) }}
                                                disabled
                                            >
                                                Primary Disabled
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Disabled state</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Secondary Buttons */}
                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Secondary Actions</h6>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-6 py-3 rounded-lg font-semibold border-2 transition-all hover:bg-opacity-5"
                                                style={{
                                                    color: getColor(1),
                                                    borderColor: getColor(1),
                                                    backgroundColor: 'transparent'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = hexToRgba(getColor(1), 0.05);
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                Secondary Large
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Outlined</span>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-5 py-2.5 rounded-md font-medium transition-all hover:bg-opacity-10"
                                                style={{
                                                    color: getColor(1),
                                                    backgroundColor: hexToRgba(getColor(1), 0.1)
                                                }}
                                            >
                                                Secondary Filled
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Subtle background</span>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-4 py-2 rounded-md font-medium transition-all hover:underline"
                                                style={{ color: getColor(1) }}
                                            >
                                                Secondary Link
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Text only</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {/* Tertiary & Utility Buttons */}
                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Tertiary & Utility</h6>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90"
                                                style={{ backgroundColor: getColor(2) }}
                                            >
                                                Tertiary Action
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Accent color</span>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <button className="px-5 py-2.5 rounded-md font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-all">
                                                Neutral Action
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">System colors</span>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <button className="px-5 py-2.5 rounded-md font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/40 transition-all">
                                                Destructive Action
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Warning/delete</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Button with Icons */}
                                <div>
                                    <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Icon Combinations</h6>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90 flex items-center gap-2"
                                                style={{ backgroundColor: getColor(0) }}
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                                </svg>
                                                Add New Item
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Left icon</span>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <button
                                                className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90 flex items-center gap-2"
                                                style={{ backgroundColor: getColor(1) }}
                                            >
                                                Download Report
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Right icon</span>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <button
                                                className="p-3 rounded-lg transition-all hover:bg-opacity-10 border-2"
                                                style={{
                                                    color: getColor(2),
                                                    borderColor: getColor(2),
                                                    backgroundColor: 'transparent'
                                                }}
                                            >
                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Icon only</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Button Groups */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">BUTTON GROUPS</span>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Segmented Controls</h6>
                                <div className="flex items-center">
                                    <button
                                        className="px-4 py-2 font-medium text-white rounded-l-lg border-r border-opacity-30"
                                        style={{ backgroundColor: getColor(0), borderColor: 'white' }}
                                    >
                                        Week
                                    </button>
                                    <button
                                        className="px-4 py-2 font-medium border-2 border-l-0 bg-white dark:bg-gray-800"
                                        style={{ color: getColor(0), borderColor: getColor(0) }}
                                    >
                                        Month
                                    </button>
                                    <button
                                        className="px-4 py-2 font-medium border-2 border-l-0 rounded-r-lg bg-white dark:bg-gray-800"
                                        style={{ color: getColor(0), borderColor: getColor(0) }}
                                    >
                                        Year
                                    </button>
                                </div>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Action Groups</h6>
                                <div className="flex gap-3">
                                    <button
                                        className="px-5 py-2.5 rounded-md font-medium text-white transition-all"
                                        style={{ backgroundColor: getColor(0) }}
                                    >
                                        Save Changes
                                    </button>
                                    <button className="px-5 py-2.5 rounded-md font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-all">
                                        Cancel
                                    </button>
                                    <button className="px-5 py-2.5 rounded-md font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-all">
                                        Delete
                                    </button>
                                </div>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Toggle Group</h6>
                                <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-1">
                                    <button className="px-3 py-1.5 text-sm font-medium text-white rounded-md transition-all" style={{ backgroundColor: getColor(1) }}>
                                        Grid
                                    </button>
                                    <button className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-md hover:text-gray-900 dark:hover:text-gray-200 transition-all">
                                        List
                                    </button>
                                    <button className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-md hover:text-gray-900 dark:hover:text-gray-200 transition-all">
                                        Card
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Form Components */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Form Components</h5>

                <div className="space-y-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Input Fields */}
                        <div className="space-y-6">
                            <div>
                                <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">INPUT FIELDS</span>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">
                                            Standard Input
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter your email"
                                            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                            style={{
                                                '--tw-ring-color': hexToRgba(getColor(0), 0.5)
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: getColor(1) }}>
                                            Labeled Input (Success)
                                        </label>
                                        <input
                                            type="text"
                                            value="john@example.com"
                                            className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-green-300 dark:border-green-600"
                                            style={{ '--tw-ring-color': 'rgba(34, 197, 94, 0.5)' }}
                                            readOnly
                                        />
                                        <p className="text-xs text-green-600 dark:text-green-400 mt-1">Email verified successfully</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">
                                            Input with Error
                                        </label>
                                        <input
                                            type="password"
                                            value="123"
                                            className="w-full px-4 py-3 border-2 border-red-300 dark:border-red-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                                        />
                                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">Password must be at least 8 characters</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">
                                            Disabled Input
                                        </label>
                                        <input
                                            type="text"
                                            value="This field is disabled"
                                            disabled
                                            className="w-full px-4 py-3 border rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Select & Other Inputs */}
                        <div className="space-y-6">
                            <div>
                                <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">SELECT & CONTROLS</span>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">
                                            Select Dropdown
                                        </label>
                                        <select
                                            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600"
                                            style={{ '--tw-ring-color': hexToRgba(getColor(0), 0.5) }}
                                        >
                                            <option>Choose an option</option>
                                            <option>Option 1</option>
                                            <option>Option 2</option>
                                            <option>Option 3</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">
                                            Textarea
                                        </label>
                                        <textarea
                                            rows="4"
                                            placeholder="Enter your message..."
                                            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 resize-none"
                                            style={{ '--tw-ring-color': hexToRgba(getColor(0), 0.5) }}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-white">
                                            Checkbox Options
                                        </label>
                                        <div className="space-y-2">
                                            {['Email notifications', 'SMS updates', 'Weekly reports'].map((option, index) => (
                                                <label key={index} className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        defaultChecked={index === 0}
                                                        className="w-5 h-5 rounded border-2 text-white focus:ring-2 transition-all"
                                                        style={{
                                                            backgroundColor: index === 0 ? getColor(0) : 'transparent',
                                                            borderColor: getColor(0),
                                                            '--tw-ring-color': hexToRgba(getColor(0), 0.5)
                                                        }}
                                                    />
                                                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">{option}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-white">
                                            Radio Buttons
                                        </label>
                                        <div className="space-y-2">
                                            {['Small', 'Medium', 'Large'].map((size, index) => (
                                                <label key={index} className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        name="size"
                                                        defaultChecked={index === 1}
                                                        className="w-4 h-4 border-2 focus:ring-2 transition-all"
                                                        style={{
                                                            color: getColor(1),
                                                            borderColor: getColor(1),
                                                            '--tw-ring-color': hexToRgba(getColor(1), 0.5)
                                                        }}
                                                    />
                                                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">{size}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Validation Example */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">FORM VALIDATION</span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="flex items-center text-sm font-medium mb-2">
                                        <span className="text-gray-900 dark:text-white">Email Address</span>
                                        <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="email"
                                            value="invalid-email"
                                            className="w-full px-4 py-3 pr-10 border-2 border-red-300 dark:border-red-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                                        />
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                            <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">Please enter a valid email address</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="flex items-center text-sm font-medium mb-2">
                                        <span className="text-gray-900 dark:text-white">Phone Number</span>
                                        <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="tel"
                                            value="+1 (555) 123-4567"
                                            className="w-full px-4 py-3 pr-10 border-2 border-green-300 dark:border-green-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                                        />
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">Phone number is valid</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Components */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Navigation Components</h5>

                <div className="space-y-8">
                    {/* Primary Navigation */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">PRIMARY NAVIGATION</span>
                        </div>

                        <div className="rounded-lg p-4" style={{ backgroundColor: hexToRgba(getColor(0), 0.05) }}>
                            <nav className="flex items-center justify-between">
                                <div className="flex items-center space-x-8">
                                    <div className="font-bold text-lg" style={{ color: getColor(0) }}>
                                        Brand
                                    </div>
                                    <div className="hidden md:flex space-x-6">
                                        <a href="#" className="font-medium transition-colors" style={{ color: getColor(0) }}>
                                            Dashboard
                                        </a>
                                        <a href="#" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors">
                                            Projects
                                        </a>
                                        <a href="#" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors">
                                            Team
                                        </a>
                                        <a href="#" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors">
                                            Reports
                                        </a>
                                    </div>
                                </div>
                                <button
                                    className="px-4 py-2 rounded-lg font-medium text-white transition-all"
                                    style={{ backgroundColor: getColor(1) }}
                                >
                                    Get Started
                                </button>
                            </nav>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">TAB NAVIGATION</span>
                        </div>

                        <div className="border-b border-gray-200 dark:border-gray-600">
                            <nav className="flex space-x-8">
                                {[
                                    { name: 'Overview', active: true },
                                    { name: 'Analytics', active: false },
                                    { name: 'Reports', active: false },
                                    { name: 'Settings', active: false }
                                ].map((tab, index) => (
                                    <button
                                        key={tab.name}
                                        className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${tab.active
                                            ? 'text-gray-900 dark:text-white'
                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                            }`}
                                        style={{
                                            borderBottomColor: tab.active ? getColor(0) : 'transparent',
                                            color: tab.active ? getColor(0) : undefined
                                        }}
                                    >
                                        {tab.name}
                                    </button>
                                ))}
                            </nav>
                        </div>
                        <div className="py-6 px-4 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Content for the active tab would appear here with relevant information and controls.
                            </p>
                        </div>
                    </div>

                    {/* Breadcrumbs */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">BREADCRUMBS</span>
                        </div>

                        <nav className="flex items-center space-x-2 text-sm">
                            <a href="#" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                                Home
                            </a>
                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                            <a href="#" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                                Projects
                            </a>
                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                            <a href="#" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                                Design System
                            </a>
                            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium" style={{ color: getColor(0) }}>
                                Components
                            </span>
                        </nav>
                    </div>

                    {/* Pagination */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">PAGINATION</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <button className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-400 cursor-not-allowed">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                </button>

                                <div className="flex space-x-1">
                                    <button
                                        className="px-3 py-2 rounded-lg font-medium text-white"
                                        style={{ backgroundColor: getColor(0) }}
                                    >
                                        1
                                    </button>
                                    <button className="px-3 py-2 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                        2
                                    </button>
                                    <button className="px-3 py-2 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                        3
                                    </button>
                                    <span className="px-3 py-2 text-gray-500">...</span>
                                    <button className="px-3 py-2 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                        10
                                    </button>
                                </div>

                                <button
                                    className="p-2 rounded-lg border-2 hover:bg-opacity-5 transition-colors"
                                    style={{
                                        color: getColor(0),
                                        borderColor: getColor(0)
                                    }}
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>

                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Showing 1-10 of 97 results
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Feedback Components */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Feedback Components</h5>

                <div className="space-y-8">
                    {/* Alert System */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">ALERT SYSTEM</span>
                        </div>

                        <div className="space-y-4">
                            {[
                                {
                                    type: 'success',
                                    title: 'Success',
                                    message: 'Your changes have been saved successfully.',
                                    color: '#10b981',
                                    bgClass: 'bg-green-50 dark:bg-green-900/20',
                                    borderClass: 'border-l-green-500'
                                },
                                {
                                    type: 'warning',
                                    title: 'Warning',
                                    message: 'Please review your settings before continuing.',
                                    color: '#f59e0b',
                                    bgClass: 'bg-yellow-50 dark:bg-yellow-900/20',
                                    borderClass: 'border-l-yellow-500'
                                },
                                {
                                    type: 'error',
                                    title: 'Error',
                                    message: 'Something went wrong. Please try again.',
                                    color: '#ef4444',
                                    bgClass: 'bg-red-50 dark:bg-red-900/20',
                                    borderClass: 'border-l-red-500'
                                },
                                {
                                    type: 'info',
                                    title: 'Information',
                                    message: 'New features are available in the latest update.',
                                    color: getColor(0),
                                    bgClass: 'bg-blue-50 dark:bg-blue-900/20',
                                    borderClass: 'border-l-blue-500'
                                }
                            ].map((alert, index) => (
                                <div
                                    key={index}
                                    className={`flex items-start p-4 rounded-lg border-l-4 ${alert.bgClass} ${alert.borderClass}`}
                                >
                                    <div className="flex-shrink-0">
                                        <div
                                            className="w-6 h-6 rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: alert.color }}
                                        >
                                            {alert.type === 'success' && (
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                            {alert.type === 'warning' && (
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                            {alert.type === 'error' && (
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                            {alert.type === 'info' && (
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                    <div className="ml-3 flex-1">
                                        <h4 className="font-semibold text-gray-900 dark:text-white">{alert.title}</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{alert.message}</p>
                                    </div>
                                    <button className="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Progress Indicators */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">PROGRESS INDICATORS</span>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Linear Progress</h6>
                                <div className="space-y-3">
                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-gray-700 dark:text-gray-300">Upload Progress</span>
                                            <span className="font-medium" style={{ color: getColor(0) }}>75%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                            <div
                                                className="h-2 rounded-full transition-all duration-300"
                                                style={{
                                                    width: '75%',
                                                    backgroundColor: getColor(0)
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-gray-700 dark:text-gray-300">Processing</span>
                                            <span className="font-medium" style={{ color: getColor(1) }}>45%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                                            <div
                                                className="h-3 rounded-full transition-all duration-300"
                                                style={{
                                                    width: '45%',
                                                    backgroundColor: getColor(1)
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Loading States</h6>
                                <div className="flex items-center space-x-6">
                                    <div className="flex items-center space-x-2">
                                        <div
                                            className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                                            style={{ borderColor: getColor(0), borderTopColor: 'transparent' }}
                                        />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Loading...</span>
                                    </div>

                                    <div className="flex space-x-1">
                                        {[0, 1, 2].map((i) => (
                                            <div
                                                key={i}
                                                className="w-2 h-2 rounded-full animate-pulse"
                                                style={{
                                                    backgroundColor: getColor(1),
                                                    animationDelay: `${i * 0.2}s`
                                                }}
                                            />
                                        ))}
                                    </div>

                                    <div className="flex space-x-1">
                                        {[0, 1, 2, 3].map((i) => (
                                            <div
                                                key={i}
                                                className="w-1 h-6 animate-pulse rounded-full"
                                                style={{
                                                    backgroundColor: getColor(2),
                                                    animationDelay: `${i * 0.15}s`
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Status Indicators */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">STATUS INDICATORS</span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Badges</h6>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        { label: 'Active', color: getColor(0) },
                                        { label: 'Pending', color: getColor(1) },
                                        { label: 'Warning', color: getColor(2) },
                                        { label: 'Error', color: '#ef4444' },
                                        { label: 'Success', color: '#10b981' }
                                    ].map((badge, index) => (
                                        <span
                                            key={index}
                                            className="px-3 py-1 rounded-full text-xs font-medium text-white"
                                            style={{ backgroundColor: badge.color }}
                                        >
                                            {badge.label}
                                        </span>
                                    ))}
                                </div>

                                <div className="mt-3 flex flex-wrap gap-2">
                                    {[
                                        { label: 'Outline Active', color: getColor(0) },
                                        { label: 'Outline Pending', color: getColor(1) },
                                        { label: 'Outline Warning', color: getColor(2) }
                                    ].map((badge, index) => (
                                        <span
                                            key={index}
                                            className="px-3 py-1 rounded-full text-xs font-medium border"
                                            style={{
                                                color: badge.color,
                                                borderColor: badge.color,
                                                backgroundColor: 'transparent'
                                            }}
                                        >
                                            {badge.label}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h6 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Status Dots</h6>
                                <div className="space-y-3">
                                    {[
                                        { status: 'Online', color: '#10b981' },
                                        { status: 'Away', color: getColor(1) },
                                        { status: 'Busy', color: getColor(2) },
                                        { status: 'Offline', color: '#6b7280' }
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-center space-x-3">
                                            <div className="relative">
                                                <div
                                                    className="w-3 h-3 rounded-full"
                                                    style={{ backgroundColor: item.color }}
                                                />
                                                <div
                                                    className="absolute inset-0 w-3 h-3 rounded-full animate-ping opacity-20"
                                                    style={{ backgroundColor: item.color }}
                                                />
                                            </div>
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{item.status}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Display Components */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Data Display</h5>

                <div className="space-y-8">
                    {/* Table */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">DATA TABLES</span>
                        </div>

                        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
                            <table className="w-full">
                                <thead style={{ backgroundColor: hexToRgba(getColor(0), 0.05) }}>
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Role
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {[
                                        { name: 'John Doe', status: 'Active', role: 'Admin', color: getColor(0) },
                                        { name: 'Jane Smith', status: 'Pending', role: 'User', color: getColor(1) },
                                        { name: 'Bob Johnson', status: 'Inactive', role: 'Editor', color: getColor(2) }
                                    ].map((row, index) => (
                                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div
                                                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                                                        style={{ backgroundColor: row.color }}
                                                    >
                                                        {row.name.split(' ').map(n => n[0]).join('')}
                                                    </div>
                                                    <div className="ml-3">
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {row.name}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full text-white"
                                                    style={{ backgroundColor: row.color }}
                                                >
                                                    {row.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                {row.role}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    className="hover:underline mr-4"
                                                    style={{ color: getColor(0) }}
                                                >
                                                    Edit
                                                </button>
                                                <button className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300">
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Cards */}
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                            <span className="text-xs font-mono text-gray-500 dark:text-gray-400">CARD COMPONENTS</span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {colors.slice(0, 3).map((color, index) => (
                                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
                                    <div className="h-2" style={{ backgroundColor: color.value }}></div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-center mb-4">
                                            <div
                                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold"
                                                style={{ backgroundColor: color.value }}
                                            >
                                                {index + 1}
                                            </div>
                                            <div className="ml-3">
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    Card {index + 1}
                                                </h3>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    Subtitle here
                                                </p>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-1">
                                            This is a sample card component showcasing consistent design patterns with your color palette.
                                        </p>
                                        <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                                            <button
                                                className="text-sm font-medium hover:underline transition-all"
                                                style={{ color: color.value }}
                                            >
                                                Learn more →
                                            </button>
                                            <div className="flex space-x-2">
                                                <button className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
                                                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                <button className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Component Specifications */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Component Specifications</h5>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                        <h6 className="text-sm font-semibold mb-4 text-gray-900 dark:text-white">Sizing Scale</h6>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Large</span>
                                <span className="text-sm font-mono">px-6 py-3 (24px, 12px)</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Medium</span>
                                <span className="text-sm font-mono">px-5 py-2.5 (20px, 10px)</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Small</span>
                                <span className="text-sm font-mono">px-4 py-2 (16px, 8px)</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Border Radius</span>
                                <span className="text-sm font-mono">4px, 6px, 8px</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h6 className="text-sm font-semibold mb-4 text-gray-900 dark:text-white">Color Mapping</h6>
                        <div className="space-y-3">
                            {colors.slice(0, 4).map((color, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <div
                                        className="w-4 h-4 rounded-full border border-gray-200 dark:border-gray-600"
                                        style={{ backgroundColor: color.value }}
                                    />
                                    <span className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                                        {['Primary actions & focus', 'Secondary actions & links', 'Accent highlights & CTAs', 'Supporting elements'][index]}
                                    </span>
                                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                                        {color.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};